import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import FormInput from '../elements/form_input';
import PhoneInput from '../elements/phone_input';
import Button from '../elements/button';
import { authApi } from '../../network/apis/auth';
import { Link } from 'react-router-dom';
import '../../assets/css/components/email_first_auth.css';

/**
 * EmailFirstAuth Component
 * A streamlined authentication flow that checks email first,
 * then shows either login or registration form accordingly.
 * 
 * @param {function} onAuthSuccess - Callback when authentication succeeds, receives user data
 * @param {string} userType - Type of user registering (vendor, doctor, lab)
 * @param {boolean} disabled - Disable all inputs
 */
const EmailFirstAuth = ({ onAuthSuccess, userType = 'professional', disabled = false }) => {
    const [step, setStep] = useState('email'); // 'email', 'login', 'register', 'otp'
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Form data
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    
    // OTP state
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [otpTimer, setOtpTimer] = useState(0);
    const otpRefs = useRef([]);
    
    // Email validation
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
    
    // Check if email exists
    const handleEmailCheck = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        setError(null);
        
        if (!email.trim()) {
            setError('Please enter your email address');
            return;
        }
        
        if (!isValidEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        
        setIsCheckingEmail(true);
        
        try {
            // Call API to check if email exists
            const response = await authApi.checkEmail(email);
            const exists = response?.data?.exists || response?.exists;
            
            if (exists) {
                setStep('login');
                setSuccess('Welcome back! Please enter your password.');
            } else {
                setStep('register');
                setSuccess('Create your account to continue.');
            }
        } catch (err) {
            // If the API doesn't exist or fails, default to showing registration
            // This is a fallback - ideally the API should exist
            console.log('Email check failed, defaulting to register:', err);
            setStep('register');
        } finally {
            setIsCheckingEmail(false);
        }
    };
    
    // Handle login
    const handleLogin = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        setError(null);
        
        if (!password) {
            setError('Please enter your password');
            return;
        }
        
        setIsLoading(true);
        
        try {
            const response = await authApi.login({ email, password });
            const responseData = response?.data?.data || response?.data || response;
            const { access, refresh, user } = responseData;
            
            localStorage.setItem('token', access);
            localStorage.setItem('refreshToken', refresh);
            localStorage.setItem('user', JSON.stringify(user));
            
            setSuccess('Login successful!');
            
            // Call success callback with user data
            if (onAuthSuccess) {
                onAuthSuccess(user, { isNewUser: false });
            }
        } catch (err) {
            const errorMessage = err.displayMessage 
                || err.response?.data?.message 
                || err.response?.data?.detail
                || 'Invalid email or password. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Handle registration
    const handleRegister = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        setError(null);
        
        // Validation
        if (!firstName.trim() || !lastName.trim()) {
            setError('Please enter your full name');
            return;
        }
        
        if (!phone.trim()) {
            setError('Please enter your phone number');
            return;
        }
        
        if (!password || password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        setIsLoading(true);
        
        try {
            const response = await authApi.register({
                username: email,
                email,
                first_name: firstName,
                last_name: lastName,
                phone,
                password
            });
            
            const responseData = response?.data?.data || response?.data || response;
            
            // Store tokens if provided
            if (responseData.access) {
                localStorage.setItem('token', responseData.access);
                localStorage.setItem('refreshToken', responseData.refresh);
                if (responseData.user) {
                    localStorage.setItem('user', JSON.stringify(responseData.user));
                }
            }
            
            setSuccess('Account created! Please verify your email.');
            setStep('otp');
            setOtpTimer(60);
        } catch (err) {
            let errorMessage = err.displayMessage || 'Registration failed. Please try again.';
            
            const errorData = err.response?.data;
            if (errorData?.errors && typeof errorData.errors === 'object') {
                const fieldErrors = Object.entries(errorData.errors);
                if (fieldErrors.length > 0) {
                    const [fieldName, messages] = fieldErrors[0];
                    const message = Array.isArray(messages) ? messages[0] : messages;
                    errorMessage = `${fieldName.replace(/_/g, ' ')}: ${message}`;
                }
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            }
            
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Handle OTP input
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        
        const newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);
        
        // Auto-focus next input
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
        
        // Auto-submit when all digits entered
        if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
            handleVerifyOtp(newOtp.join(''));
        }
    };
    
    // Handle OTP keydown for backspace
    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };
    
    // Verify OTP
    const handleVerifyOtp = async (otpCode = null) => {
        const code = otpCode || otp.join('');
        
        if (code.length !== 6) {
            setError('Please enter the complete 6-digit code');
            return;
        }
        
        setError(null);
        setIsLoading(true);
        
        try {
            const response = await authApi.verifyOtp({ email, otp: code });
            const responseData = response?.data?.data || response?.data || response;
            
            // Update tokens if new ones provided
            if (responseData.access) {
                localStorage.setItem('token', responseData.access);
                localStorage.setItem('refreshToken', responseData.refresh);
            }
            
            const user = responseData.user || JSON.parse(localStorage.getItem('user') || '{}');
            if (responseData.user) {
                localStorage.setItem('user', JSON.stringify(user));
            }
            
            setSuccess('Email verified successfully!');
            
            // Call success callback
            if (onAuthSuccess) {
                onAuthSuccess(user, { isNewUser: true });
            }
        } catch (err) {
            const errorMessage = err.displayMessage 
                || err.response?.data?.message 
                || 'Invalid verification code. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Resend OTP
    const handleResendOtp = async () => {
        if (otpTimer > 0) return;
        
        setError(null);
        setIsLoading(true);
        
        try {
            await authApi.resendOtp({ email });
            setSuccess('Verification code sent!');
            setOtpTimer(60);
            setOtp(['', '', '', '', '', '']);
        } catch (err) {
            setError('Failed to resend code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };
    
    // OTP timer countdown
    useEffect(() => {
        if (otpTimer > 0) {
            const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [otpTimer]);
    
    // Go back to email step
    const handleChangeEmail = () => {
        setStep('email');
        setPassword('');
        setError(null);
        setSuccess(null);
    };
    
    return (
        <div className="email-first-auth">
            {/* Error/Success Messages */}
            {error && (
                <div className="auth-alert auth-alert-error">
                    <span className="auth-alert-icon">⚠️</span>
                    <span className="auth-alert-message">{error}</span>
                    <button type="button" className="auth-alert-close" onClick={() => setError(null)}>×</button>
                </div>
            )}
            {success && (
                <div className="auth-alert auth-alert-success">
                    <span className="auth-alert-icon">✓</span>
                    <span className="auth-alert-message">{success}</span>
                </div>
            )}
            
            {/* Step 1: Email Input */}
            {step === 'email' && (
                // <form onSubmit={handleEmailCheck} className="auth-step-form">
                <div>
                    <div className="auth-step-header">
                        <h3>Let&apos;s get started</h3>
                        <p>Enter your email to continue as a {userType}</p>
                    </div>
                    
                    <FormInput
                        id="auth-email"
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        disabled={disabled || isCheckingEmail}
                        autoFocus
                        showLabel
                    />
                    
                    <Button
                        text={isCheckingEmail ? 'Checking...' : 'Continue'}
                        // type="submit"
                            onClick={handleEmailCheck}
                        fullWidth
                        loading={isCheckingEmail}
                        disabled={disabled || isCheckingEmail || !email.trim()}
                    />
                    
                    <p className="auth-helper-text">
                        We&apos;ll check if you already have an account
                    </p>
                {/* </form> */}
                </div>
            )}
            
            {/* Step 2a: Login (existing user) */}
            {step === 'login' && (
                // <form onSubmit={handleLogin} className="auth-step-form">
                <div className="auth-step-form">
                    <div className="auth-step-header">
                        <h3>Welcome back!</h3>
                        <p>Enter your password to continue</p>
                    </div>
                    
                    <div className="email-display">
                        <span className="email-label">Email</span>
                        <span className="email-value">{email}</span>
                        <button type="button" className="change-email-btn" onClick={handleChangeEmail}>
                            Change
                        </button>
                    </div>
                    
                    <FormInput
                        id="auth-password"
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        disabled={disabled || isLoading}
                        autoFocus
                        showLabel
                    />
                    
                    <div className="forgot-password-row">
                        <Link to="/forgot-password" className="forgot-password-link">
                            Forgot password?
                        </Link>
                    </div>
                    
                    <Button
                        text={isLoading ? 'Signing in...' : 'Sign In & Continue'}
                        // type="submit"
                        onClick={handleLogin}
                        fullWidth
                        loading={isLoading}
                        disabled={disabled || isLoading}
                    />
                {/* </form> */}
                </div>
            )}
            
            {/* Step 2b: Registration (new user) */}
            {step === 'register' && (
                <div className="auth-step-form">
                    <div className="auth-step-header">
                        <h3>Create your account</h3>
                        <p>Fill in your details to get started</p>
                    </div>
                    
                    <div className="email-display">
                        <span className="email-label">Email</span>
                        <span className="email-value">{email}</span>
                        <button type="button" className="change-email-btn" onClick={handleChangeEmail}>
                            Change
                        </button>
                    </div>
                    
                    <div className="form-row">
                        <FormInput
                            id="auth-firstname"
                            label="First Name"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First name"
                            disabled={disabled || isLoading}
                            showLabel
                        />
                        <FormInput
                            id="auth-lastname"
                            label="Last Name"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last name"
                            disabled={disabled || isLoading}
                            showLabel
                        />
                    </div>
                    
                    <PhoneInput
                        label="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value || e)}
                        disabled={disabled || isLoading}
                        showLabel
                        defaultCountryCode="+234"
                    />
                    
                    <FormInput
                        id="auth-new-password"
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password (min. 8 characters)"
                        disabled={disabled || isLoading}
                        showLabel
                    />
                    
                    <FormInput
                        id="auth-confirm-password"
                        label="Confirm Password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        disabled={disabled || isLoading}
                        showLabel
                    />
                    
                    <Button
                        text={isLoading ? 'Creating account...' : 'Create Account & Continue'}
                        // type="submit"
                        onClick={handleRegister}
                        fullWidth
                        loading={isLoading}
                        disabled={disabled || isLoading}
                    />
                    
                    <p className="auth-terms-text">
                        By creating an account, you agree to our{' '}
                        <Link to="/terms">Terms of Service</Link> and{' '}
                        <Link to="/privacy">Privacy Policy</Link>
                    </p>
                {/* </form> */}
                </div>
            )}
            
            {/* Step 3: OTP Verification */}
            {step === 'otp' && (
                <div className="auth-step-form">
                    <div className="auth-step-header">
                        <h3>Verify your email</h3>
                        <p>Enter the 6-digit code sent to <strong>{email}</strong></p>
                    </div>
                    
                    <div className="otp-input-group">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (otpRefs.current[index] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                className="otp-input"
                                disabled={disabled || isLoading}
                                autoFocus={index === 0}
                            />
                        ))}
                    </div>
                    
                    <Button
                        text={isLoading ? 'Verifying...' : 'Verify & Continue'}
                        onClick={() => handleVerifyOtp()}
                        fullWidth
                        loading={isLoading}
                        disabled={disabled || isLoading || otp.join('').length !== 6}
                    />
                    
                    <div className="resend-section">
                        {otpTimer > 0 ? (
                            <p className="resend-timer">Resend code in {otpTimer}s</p>
                        ) : (
                            <button 
                                type="button" 
                                className="resend-btn"
                                onClick={handleResendOtp}
                                disabled={isLoading}
                            >
                                Didn&apos;t receive the code? Resend
                            </button>
                        )}
                    </div>
                    
                    <button type="button" className="change-email-btn-full" onClick={handleChangeEmail}>
                        Use a different email
                    </button>
                </div>
            )}
        </div>
    );
};

EmailFirstAuth.propTypes = {
    onAuthSuccess: PropTypes.func.isRequired,
    userType: PropTypes.string,
    disabled: PropTypes.bool
};

export default EmailFirstAuth;
